import * as React from 'react';
import {ApolloProvider} from 'react-apollo';
import {Helmet} from 'react-helmet';
import {Provider, useDispatch} from 'react-redux';

import {ApolloProvider as ReactHooksApolloProvider} from '@apollo/react-hooks';
// @ts-expect-error
import {PersistGate} from 'redux-persist/lib/integration/react';

import {headTitle} from 'shells/document/head-title';
import type {NutshellTour} from 'shells/tours/types';
import {useNutshellTour} from 'shells/tours/use-nutshell-tour';

import {nutApolloClient} from '../../apollo-client';
import {store, persistor} from '../../store';

// @ts-expect-error
import * as actions from './dashboard-actions';
import {ACTIVITIES, TODOS, FEED} from './dashboard-constants';
import type {TabKey} from './dashboard-constants';
// @ts-expect-error
import {DashboardPanes} from './panes/dashboard-panes';

type Props = {
    tab?: string | null | undefined;
    resetActivities?: boolean;
    tour?: NutshellTour | null | undefined;
};

function getViewFromTab(tab: string): TabKey {
    switch (tab) {
        case 'todos':
            return TODOS;
        case 'feed':
            return FEED;
        case 'activities':
        default:
            return ACTIVITIES;
    }
}

export function MasterDashboard(props: Props): React.ReactElement {
    const dispatch = useDispatch();
    const {startTour} = useNutshellTour();

    // If a tab is provided, set the active view to the corresponding tab
    // Then clear the tab param from the URL to keep URL in sync
    React.useEffect(() => {
        if (props.tab) {
            const view = getViewFromTab(props.tab);

            const url = new URL(window.location.href);
            url.searchParams.delete('tab');
            window.history.replaceState({}, '', url.toString());

            dispatch(actions.changeRightPaneActiveView(view));
        }
    }, [props.tab, dispatch]);

    // Start tour if provided via route state
    React.useEffect(() => {
        let timeout: ReturnType<typeof setTimeout> | null = null;
        if (props.tour) {
            const tourId = props.tour;
            timeout = setTimeout(() => {
                startTour(tourId);
            }, 2000);
        }

        return () => {
            if (timeout) {
                clearTimeout(timeout);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Reset activities filters, calendar mode, and display date to defaults
    React.useEffect(() => {
        if (props.resetActivities) {
            const url = new URL(window.location.href);
            url.searchParams.delete('resetActivities');
            window.history.replaceState({}, '', url.toString());

            dispatch(actions.updateActivityFilter(''));
            dispatch(actions.resetActivityCalendarDisplayMode());
            dispatch(actions.updateActivityDisplayDate(new Date()));
        }
    }, [props.resetActivities, dispatch]);

    return (
        <>
            <Helmet>
                <title>{headTitle('Dashboard')}</title>
            </Helmet>
            <ApolloProvider client={nutApolloClient}>
                <ReactHooksApolloProvider client={nutApolloClient}>
                    <Provider store={store}>
                        <PersistGate persistor={persistor}>
                            <DashboardPanes />
                        </PersistGate>
                    </Provider>
                </ReactHooksApolloProvider>
            </ApolloProvider>
        </>
    );
}
